/**
 * Pharma name aliasing (frontend mirror of backend/utils/pharma_aliases.py).
 *
 * Used by AI Mapping's `findMatchingContacts` so Janssen-titled candidates
 * still match the Johnson & Johnson contacts already in the CRM (and
 * Vifor → CSL, etc.). Kept thin and identical to the Python side — if you
 * add a group here, add the same group there.
 */

const ALIAS_GROUPS = [
  // Johnson & Johnson family — Janssen is J&J's pharmaceutical division
  [
    'Johnson and Johnson',
    'Johnson & Johnson',
    'J&J',
    'JNJ',
    'Janssen',
    'Janssen Research & Development',
    'Janssen R&D',
    'Janssen Pharmaceuticals',
    'Janssen Biotech',
    'Janssen Pharmaceutica',
    'Jansen',
  ],
  // CSL Vifor — Vifor was acquired by CSL Limited (2022)
  [
    'CSL',
    'CSL Limited',
    'CSL Pharma',
    'CSL Behring',
    'CSL Vifor',
    'Vifor',
    'Vifor Pharma',
    'Vifor Pharmaceuticals',
  ],
  // Merck & Co (US) — NEVER merged with Merck KGaA (Darmstadt)
  ['Merck & Co', 'Merck & Co Inc', 'Merck & Co., Inc. (MSD)', 'Merck & Co (MSD)', 'MSD', 'Merck Sharp & Dohme', 'Merck Sharp and Dohme'],
  // Merck KGaA (Darmstadt) family
  ['Merck KGaA', 'Merck KGaA, Darmstadt, Germany', 'Merck Group', 'Merck Healthcare', 'EMD Serono', 'Merck Serono'],
  // Roche / Genentech
  ['Roche', 'F. Hoffmann-La Roche', 'Hoffmann-La Roche', 'Roche Pharma', 'Genentech'],
  // Junshi Biosciences / TopAlliance
  ['Junshi Biosciences', 'Shanghai Junshi Biosciences', 'Junshi Pharma', 'Junshi Pharma / TopAlliance', 'TopAlliance', 'TopAlliance Biosciences'],
];

const SUFFIX_PATTERNS = [
  /\s+(inc|incorporated|corp|corporation|company|co)\.?$/i,
  /\s+(ltd|limited|pte|pty|llc|llp|lp|gmbh|ag|sa|nv|bv|kk)\.?$/i,
  /\s+(holdings?|group|usa|us|asia|china|japan|korea|europe|emea|apac)\.?$/i,
  /\s+(pharma|pharmaceuticals?|biotech|biopharma|therapeutics|biosciences|healthcare)\.?$/i,
  /\s+r\s*&\s*d$/i,
  /\s+(research|development)$/i,
];

function normalizeForAlias(name) {
  if (!name) return '';
  let n = name.trim().toLowerCase();
  n = n.replace(/[.,;:'"()]/g, '');
  // Apply suffix strip twice — "Janssen Pharma R&D" ends in two suffixes
  for (let pass = 0; pass < 2; pass += 1) {
    for (const pat of SUFFIX_PATTERNS) n = n.replace(pat, '');
  }
  n = n.replace(/\s+/g, ' ').trim();
  n = n.replace(/ & /g, ' and ');
  return n;
}

const ALIAS_LOOKUP = (() => {
  const lookup = {};
  for (const group of ALIAS_GROUPS) {
    const canonicalKey = normalizeForAlias(group[0]);
    for (const alias of group) {
      const key = normalizeForAlias(alias);
      if (key) lookup[key] = canonicalKey;
    }
  }
  return lookup;
})();

/**
 * Stable, comparable key for a pharma name. Two names that resolve to the
 * same key are treated as the same entity.
 */
export function canonicalPharmaKey(name) {
  const norm = normalizeForAlias(name);
  return ALIAS_LOOKUP[norm] || norm;
}

export function areSamePharma(a, b) {
  if (!a || !b) return false;
  return canonicalPharmaKey(a) === canonicalPharmaKey(b);
}
